import React, { useState } from 'react';
import { Card, CardContent } from '../../../../../components/ui/card';
import { Button } from '../../../../../components/ui/button';
import { Plus, FileText, Download } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../../components/ui/select";
import { Label } from "../../../../../components/ui/label";
import { Input } from "../../../../../components/ui/input";
import type { SpecificationDocument } from '../../../types/index';

interface WeldProcessDetails {
  processType: string;
  wireType: string;
  wireSize: string;
  shieldingGas: string;
  flowRate: string;
  voltage: string;
  amperage: string;
}

interface SpecificationDetails {
  weldProcess?: WeldProcessDetails;
  inspectionProcedure?: {
    method: string;
    equipment: string;
    frequency: string;
    coverage: string;
    calibrationRequirements: string;
    standardName: string;
  };
  acceptanceCriteria?: {
    standard: string;
    defectTypes: string[];
    tolerances: string;
    repairProcedure: string;
  };
  passConfig?: {
    numberOfPasses: number;
    passes: Array<{
      name: string;
      sequence: number;
      requirements: string;
    }>;
  };
  fitmentReadings?: {
    gapTolerance: string;
    hiLowTolerance: string;
    ovality: string;
    straightness: string;
    measurementPoints: number;
  };
  weldingPosition?: {
    position: string;
    rotationAllowed: boolean;
    maxRotationAngle: string;
    fixedPosition: string;
    restrictions: string[];
  };
  bevel?: {
    type: string;
    angle: string;
    rootFace: string;
    rootGap: string;
    landThickness: string;
    preparation: string;
  };
}

interface AddNewSpecificationProps {
  onAdd: (spec: SpecificationDocument) => void;
  onClose: () => void;
  isOpen: boolean;
}

const SPEC_TYPE_LABELS = {
  'WPS': 'Welding Procedure Specification',
  'drawing': 'Drawing',
  'weld-process': 'Weld Process',
  'inspection-procedure': 'Inspection Procedure',
  'acceptance-criteria': 'Acceptance Criteria',
  'pass-config': 'Pass Configuration',
  'fitment-readings': 'Fitment Readings',
  'welding-position': 'Welding Position',
  'bevel': 'Bevel Details'
} as const;

export const AddNewSpecification: React.FC<AddNewSpecificationProps> = ({ onAdd, onClose, isOpen }) => {
  const [newSpecification, setNewSpecification] = useState<{
    type: SpecificationDocument['type'] | '';
    file: File | null;
    pipeId: string;
    details?: SpecificationDetails;
  }>({
    type: '',
    file: null,
    pipeId: '',
    details: {
      weldProcess: {
        processType: '',
        wireType: '',
        wireSize: '',
        shieldingGas: '',
        flowRate: '',
        voltage: '',
        amperage: ''
      }
    }
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setNewSpecification(prev => ({
        ...prev,
        file: e.target.files![0]
      }));
    }
  };

  const handleSubmit = () => {
    if (newSpecification.file && newSpecification.type && newSpecification.pipeId) {
      const newDoc: SpecificationDocument = {
        id: Math.random().toString(36).substr(2, 9),
        fileName: newSpecification.file.name,
        type: newSpecification.type as SpecificationDocument['type'],
        pipeId: newSpecification.pipeId,
        uploadDate: new Date().toISOString(),
        details: newSpecification.details
      };
      onAdd(newDoc);
      setNewSpecification({
        type: '',
        file: null,
        pipeId: '',
        details: {
          weldProcess: {
            processType: '',
            wireType: '',
            wireSize: '',
            shieldingGas: '',
            flowRate: '',
            voltage: '',
            amperage: ''
          }
        }
      });
      onClose();
    }
  };

  const updateWeldProcessField = (field: keyof WeldProcessDetails, value: string) => {
    setNewSpecification(prev => ({
      ...prev,
      details: {
        ...prev.details,
        weldProcess: {
          processType: prev.details?.weldProcess?.processType || '',
          wireType: prev.details?.weldProcess?.wireType || '',
          wireSize: prev.details?.weldProcess?.wireSize || '',
          shieldingGas: prev.details?.weldProcess?.shieldingGas || '',
          flowRate: prev.details?.weldProcess?.flowRate || '',
          voltage: prev.details?.weldProcess?.voltage || '',
          amperage: prev.details?.weldProcess?.amperage || '',
          [field]: value
        }
      }
    }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add New Specification</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Specification Type</Label>
            <Select
              value={newSpecification.type}
              onValueChange={(value: SpecificationDocument['type']) => 
                setNewSpecification(prev => ({ ...prev, type: value }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(SPEC_TYPE_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Pipe ID</Label>
            <Input
              value={newSpecification.pipeId}
              onChange={(e) => setNewSpecification(prev => ({ ...prev, pipeId: e.target.value }))}
              placeholder="Enter pipe ID"
            />
          </div>

          <div className="space-y-2">
            <Label>File</Label>
            <Input
              type="file"
              onChange={handleFileChange}
              accept=".pdf,.doc,.docx"
            />
          </div>

          {/* Dynamic form fields based on specification type */}
          {newSpecification.type === 'weld-process' && (
            <div className="space-y-4">
              <h4 className="font-medium">Weld Process Details</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Process Type</Label>
                  <Input
                    value={newSpecification.details?.weldProcess?.processType || ''}
                    onChange={(e) => updateWeldProcessField('processType', e.target.value)}
                    placeholder="e.g., GMAW"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Wire Type</Label>
                  <Input
                    value={newSpecification.details?.weldProcess?.wireType || ''}
                    onChange={(e) => updateWeldProcessField('wireType', e.target.value)}
                    placeholder="e.g., ER70S-6"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Wire Size</Label>
                  <Input
                    value={newSpecification.details?.weldProcess?.wireSize || ''}
                    onChange={(e) => updateWeldProcessField('wireSize', e.target.value)}
                    placeholder="e.g., 0.035"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Shielding Gas</Label>
                  <Input
                    value={newSpecification.details?.weldProcess?.shieldingGas || ''}
                    onChange={(e) => updateWeldProcessField('shieldingGas', e.target.value)}
                    placeholder="e.g., 75/25 Ar/CO2"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Flow Rate</Label>
                  <Input
                    value={newSpecification.details?.weldProcess?.flowRate || ''}
                    onChange={(e) => updateWeldProcessField('flowRate', e.target.value)}
                    placeholder="e.g., 20-25 CFH"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Voltage</Label>
                  <Input
                    value={newSpecification.details?.weldProcess?.voltage || ''}
                    onChange={(e) => updateWeldProcessField('voltage', e.target.value)}
                    placeholder="e.g., 18-22V"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Amperage</Label>
                  <Input
                    value={newSpecification.details?.weldProcess?.amperage || ''}
                    onChange={(e) => updateWeldProcessField('amperage', e.target.value)}
                    placeholder="e.g., 180-220A"
                  />
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit}
              disabled={!newSpecification.file || !newSpecification.type || !newSpecification.pipeId}
            >
              Add Specification
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}; 